// src/lib/theme.tsx
//
// ThemeProvider — three-mode appearance system (light / dark / sepia) with an
// optional 'system' that follows the OS prefers-color-scheme.
//
// Precedence cascade for the active theme on mount:
//   1. The anti-FOUC inline script in index.html already set data-theme on the
//      html element from localStorage before React mounted — that's the visual
//      truth at first paint. React's first effect mirrors the same value into
//      provider state, so it picks up where the script left off without flash.
//   2. On auth state change (sign-in / token refresh / user update), the
//      provider fetches profiles.appearance. If it's non-null and differs from
//      current theme, that wins — DB is the cross-device source of truth.
//
// Writes go to BOTH localStorage and profiles.appearance:
//   - localStorage write is synchronous, drives next-page-load FOUC mitigation.
//   - profiles write is fire-and-forget; failure logs to console (no toast — a
//     "couldn't save theme" toast on every flip would be more annoying than the
//     bug it surfaces, and the user's next load falls back to localStorage).
//
// 'system' resolution: at apply time, matchMedia decides light vs dark. Sepia
// is never resolved automatically — it's only ever an explicit pin. A change
// listener on the media query updates resolvedTheme live while theme==='system'.

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

export type Theme = 'light' | 'dark' | 'sepia' | 'system'
export type ResolvedTheme = 'light' | 'dark' | 'sepia'

const STORAGE_KEY = 'orkestra-theme'

type ThemeContextValue = {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (next: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredTheme(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'sepia' || raw === 'system') return raw
  } catch { /* SSR / private mode — fall through */ }
  return 'system'
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    const prefersDark = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'dark' : 'light'
  }
  return theme
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved)
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme())
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(readStoredTheme()))
  const user = useAuth(s => s.user)
  const profile = useAuth(s => s.profile)
  // Picker-race guard: sync from profile.appearance ONCE per profile.id, on
  // first load (sign-in or page mount with an existing session). After that,
  // the user's in-session picker choice is authoritative. Without this gate
  // the effect re-fires every time `theme` changes and clobbers the local
  // pick with the stale profile value (the optimistic Supabase write is
  // fire-and-forget — we don't re-fetch the profile, so it stays stale).
  // Re-runs on user-switch (different profile.id → re-sync) and clears on
  // sign-out so the next sign-in re-syncs.
  const lastSyncedProfileIdRef = useRef<string | null>(null)

  // Apply on mount and on every theme change. resolveTheme handles 'system'.
  useEffect(() => {
    const resolved = resolveTheme(theme)
    setResolvedTheme(resolved)
    applyTheme(resolved)
  }, [theme])

  // OS preference change listener — only relevant while theme === 'system'.
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const resolved: ResolvedTheme = mq.matches ? 'dark' : 'light'
      setResolvedTheme(resolved)
      applyTheme(resolved)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  // When a user signs in (or their profile loads), pull profiles.appearance.
  // Cross-device truth wins over localStorage — but only on first load per
  // profile.id (see lastSyncedProfileIdRef rationale above).
  useEffect(() => {
    if (!profile) {
      lastSyncedProfileIdRef.current = null
      return
    }
    if (lastSyncedProfileIdRef.current === profile.id) return
    lastSyncedProfileIdRef.current = profile.id
    const remote = (profile as any).appearance as Theme | null
    if (!remote || remote === theme) return
    setThemeState(remote)
    try { localStorage.setItem(STORAGE_KEY, remote) } catch { /* ignore */ }
  }, [profile, theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
    if (user?.id) {
      // Fire-and-forget — UI is already updated optimistically.
      supabase
        .from('profiles')
        .update({ appearance: next })
        .eq('id', user.id)
        .then(({ error }) => { if (error) console.error('[theme] failed to persist appearance:', error.message) })
    }
  }, [user?.id])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
